import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import CustomDataGrid from "./CustomDataGrid";

const readPath = (object, path) =>
  String(path || "")
    .split(".")
    .reduce((value, key) => value?.[key], object);

export const ColumnDirective = () => null;
export const ColumnsDirective = ({ children }) => <>{children}</>;
export const Inject = () => null;
export const Page = () => null;
export const Toolbar = () => null;
export const ColumnMenu = () => null;
export const Sort = () => null;
export const Resize = () => null;
export const Filter = () => null;
export const ExcelExport = () => null;
export const PdfExport = () => null;
export const Edit = () => null;
export const CommandColumn = () => null;

const findColumnDirectives = (children, result = []) => {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === ColumnDirective) result.push(child);
    else if (child.props?.children)
      findColumnDirectives(child.props.children, result);
  });
  return result;
};

export const GridComponent = forwardRef(
  (
    { children, dataSource = [], pageSettings, toolbar, ...props },
    ref
  ) => {
    const directives = useMemo(
      () => findColumnDirectives(children),
      [children]
    );
    const columns = useMemo(
      () =>
        directives.map((directive, index) => {
          const { field, headerText, template, width, type, format } =
            directive.props;
          const id = field || `legacy-column-${index}`;
          return {
            id,
            header: headerText || field || `Column ${index + 1}`,
            accessorFn: (row) => readPath(row, field),
            size: Number.parseInt(width, 10) || 150,
            flex: 1,
            cell: template
              ? ({ row, getValue }) => template(row.original)
              : ({ getValue }) => {
                  const value = getValue();
                  if (!value || !format || type !== "date") return value ?? "—";
                  const date = new Date(value);
                  return Number.isNaN(date.getTime())
                    ? value
                    : date.toLocaleString();
                },
          };
        }),
      [directives]
    );

    useImperativeHandle(
      ref,
      () => ({
        csvExport: () => {},
        excelExport: () => {},
        pdfExport: () => {},
        refresh: () => {},
      }),
      []
    );

    return (
      <CustomDataGrid
        data={Array.isArray(dataSource) ? dataSource : []}
        columns={columns}
        initialPageSize={pageSettings?.pageSize || 15}
        showToolbar={Boolean(toolbar)}
        {...props}
      />
    );
  }
);

export const UploaderComponent = forwardRef(
  ({ allowedExtensions, selected, multiple, id, ...props }, ref) => {
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => ({
      clearAll: () => {
        if (inputRef.current) inputRef.current.value = "";
      },
      dataBind: () => {},
      element: inputRef.current,
      dropArea: null,
    }));

    return (
      <input
        {...props}
        ref={inputRef}
        id={id}
        type="file"
        accept={allowedExtensions}
        multiple={multiple}
        onChange={(event) =>
          selected?.({
            filesData: Array.from(event.target.files || []).map((rawFile) => ({
              rawFile,
            })),
          })
        }
      />
    );
  }
);

export const enableRipple = () => {};

export const RichTextEditorComponent = ({
  value = "",
  change,
  height = 250,
}) => (
  <textarea
    className="form-control"
    style={{ height, resize: "vertical" }}
    value={value}
    onChange={(event) => change?.({ value: event.target.value })}
  />
);

export const HtmlEditor = () => null;
export const Image = () => null;
export const Link = () => null;
export const QuickToolbar = () => null;
export const Table = () => null;
export const Video = () => null;
export const Audio = () => null;
export const PasteCleanup = () => null;

export const ItemDirective = () => null;
export const ItemsDirective = ({ children }) => <>{children}</>;
export const ToolbarComponent = ({ children }) => {
  const items = [];

  const collectItems = (nodes) =>
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === ItemDirective) items.push(child.props);
      else if (child.props?.children) collectItems(child.props.children);
    });
  collectItems(children);
  return (
    <div className="d-flex gap-2 border-bottom p-2">
      {items
        .filter((item) => item.type !== "Separator")
        .map((item, index) => (
          <button
            className="btn btn-outline-primary btn-sm"
            key={`${item.text}-${index}`}
            type="button"
          >
            {item.text}
          </button>
        ))}
    </div>
  );
};

export const TreeViewComponent = ({ fields }) => (
  <ul className="list-unstyled">
    {(fields?.dataSource || []).map((item) => (
      <li key={item.id || item.text}>{item.text || item.name}</li>
    ))}
  </ul>
);
export const ChatUIComponent = ({ children }) => (
  <div className="border rounded p-2">{children}</div>
);
export const MessagesDirective = ({ children }) => <>{children}</>;
export const MessageDirective = ({ text }) => <p>{text}</p>;
export const UserModel = () => null;
export class DataManager {
  constructor(dataSource = []) {
    this.dataSource = dataSource;
  }
}
export const ButtonComponent = ({ children, ...props }) => (
  <button type="button" {...props}>
    {children}
  </button>
);
